/**
 * Browser (Client Component) 用 API クライアント
 * ドメイン認証: ブラウザが自動送信する Origin ヘッダーで Product を識別
 */

import { getConfig } from "@/config/app.config";
import type { FeedbackSummary } from "@/types";
import { encodeSlug } from "./slug";

const API_URL = getConfig().publicApiUrl;

async function fetchApi<T>(path: string, init?: RequestInit): Promise<T> {
  const url = `${API_URL}${path}`;
  const res = await fetch(url, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...init?.headers,
    },
  });

  if (!res.ok) {
    throw new Error(`API error: ${res.status} ${res.statusText}`);
  }

  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- res.json() returns Promise<any>
  return res.json() as Promise<T>;
}

export async function postFeedback(
  slug: string,
  isHelpful: boolean,
  reason?: string,
  comment?: string,
): Promise<{ id: string }> {
  const body: Record<string, unknown> = { isHelpful };
  if (reason) body["reason"] = reason;
  if (comment) body["comment"] = comment;

  return fetchApi<{ id: string }>(`/api/external/v1/articles/${encodeSlug(slug)}/feedback`, {
    method: "POST",
    credentials: "include",
    body: JSON.stringify(body),
  });
}

export async function getFeedbackSummary(slug: string): Promise<FeedbackSummary> {
  return fetchApi<FeedbackSummary>(
    `/api/external/v1/articles/${encodeSlug(slug)}/feedback/summary`,
  );
}

/**
 * サジェスト記事候補の型
 */
export interface SuggestArticle {
  id: string;
  title: string;
  slug: string;
  snippet: string | null;
  snippetHighlights: { start: number; length: number }[];
  titleHighlights: { start: number; length: number }[];
  category: { id: string; name: string; slug: string } | null;
}

/**
 * サジェストレスポンスの型
 */
export interface SuggestResult {
  keywords: string[];
  articles: SuggestArticle[];
}

/**
 * サジェストキーワードと記事候補を取得
 * @param prefix 検索プレフィックス
 * @param limit キーワード取得件数（デフォルト10）
 * @param articleLimit 記事候補取得件数（デフォルト3）
 */
export async function getSuggest(
  prefix: string,
  limit: number = 10,
  articleLimit: number = 3,
): Promise<SuggestResult> {
  const params = new URLSearchParams();
  params.set("q", prefix);
  if (limit !== 10) params.set("limit", String(limit));
  if (articleLimit !== 3) params.set("articleLimit", String(articleLimit));
  return fetchApi<SuggestResult>(`/api/external/v1/suggest?${params.toString()}`);
}

/**
 * 記事の閲覧ログを記録する（fire-and-forget 用途）
 * credentials: "include" で _faq_vid Cookie を送信する
 */
export async function recordArticleView(slug: string): Promise<void> {
  const url = `${API_URL}/api/external/v1/articles/${encodeSlug(slug)}/view`;
  await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
  });
}
